import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// ── Base API calls ────────────────────────────────────────────────────────────
const waApi = {
  getStats:       ()         => api.get('/api/admin/marketing/whatsapp/stats'),
  getAnalytics:   (days = 30) => api.get('/api/admin/marketing/whatsapp/analytics', { params: { days } }),
  listCampaigns:  (params)   => api.get('/api/admin/marketing/whatsapp/campaigns', { params }),
  getCampaign:    (id)       => api.get(`/api/admin/marketing/whatsapp/campaigns/${id}`),
  createCampaign: (data)     => api.post('/api/admin/marketing/whatsapp/campaigns', data),
  deleteCampaign: (id)       => api.delete(`/api/admin/marketing/whatsapp/campaigns/${id}`),
  sendCampaign:   (id)       => api.post(`/api/admin/marketing/whatsapp/campaigns/${id}/send`),
  retryFailed:    (id)       => api.post(`/api/admin/marketing/whatsapp/campaigns/${id}/retry-failed`),
  getMessages:    (id, p)    => api.get(`/api/admin/marketing/whatsapp/campaigns/${id}/messages`, { params: p }),
  // P1: Templates
  listTemplates:  (params)   => api.get('/api/admin/marketing/whatsapp/templates', { params }),
  syncTemplates:  ()         => api.post('/api/admin/marketing/whatsapp/templates/sync'),
  // P1: Inbox
  getInbox:       (params)   => api.get('/api/admin/marketing/whatsapp/inbox', { params }),
  // P2: Segmentation preview
  previewSegment: (data)     => api.post('/api/admin/marketing/whatsapp/segments/preview', data),
  expandSegment:  (data)     => api.post('/api/admin/marketing/whatsapp/segments/contacts', data),
};

export function useWAStats() {
  return useQuery({
    queryKey: ['wa-stats'],
    queryFn: () => waApi.getStats(),
    select: (res) => res.data,
    refetchInterval: 30000,
  });
}

export function useWACampaigns(params = {}) {
  return useQuery({
    queryKey: ['wa-campaigns', params],
    queryFn: () => waApi.listCampaigns(params),
    select: (res) => res.data,
    refetchInterval: 10000,
  });
}

export function useWACampaign(id) {
  return useQuery({
    queryKey: ['wa-campaign', id],
    queryFn: () => waApi.getCampaign(id),
    select: (res) => res.data,
    enabled: !!id,
    refetchInterval: 5000,
  });
}

export function useWACampaignMessages(campaignId, params = {}) {
  return useQuery({
    queryKey: ['wa-messages', campaignId, params],
    queryFn: () => waApi.getMessages(campaignId, params),
    select: (res) => res.data,
    enabled: !!campaignId,
    refetchInterval: 8000,
  });
}

export function useCreateWACampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => waApi.createCampaign(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wa-campaigns'] }),
  });
}

export function useDeleteWACampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => waApi.deleteCampaign(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wa-campaigns'] }),
  });
}

export function useSendWACampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => waApi.sendCampaign(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['wa-campaigns'] });
      qc.invalidateQueries({ queryKey: ['wa-campaign', id] });
    },
  });
}

export function useRetryWAFailed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => waApi.retryFailed(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['wa-campaign', id] });
      qc.invalidateQueries({ queryKey: ['wa-messages', id] });
    },
  });
}


// ── P2: Analytics (timeseries + top templates + status mix) ──────────────────
export function useWAAnalytics(days = 30) {
  return useQuery({
    queryKey: ['wa-analytics', days],
    queryFn: () => waApi.getAnalytics(days),
    select: (res) => res.data,
    refetchInterval: 60000,
  });
}

// ── P1: Templates (synced from Meta) ─────────────────────────────────────────
export function useWATemplates(params = {}) {
  return useQuery({
    queryKey: ['wa-templates', params],
    queryFn: () => waApi.listTemplates(params),
    select: (res) => res.data,
  });
}

export function useSyncWATemplates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => waApi.syncTemplates(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wa-templates'] });
      qc.invalidateQueries({ queryKey: ['wa-stats'] });
    },
  });
}

// ── P1: Inbox (inbound replies + opt-outs) ───────────────────────────────────
export function useWAInbox(params = {}) {
  return useQuery({
    queryKey: ['wa-inbox', params],
    queryFn: () => waApi.getInbox(params),
    select: (res) => res.data,
    refetchInterval: 30000,
  });
}

// ── P2: Segmentation preview ─────────────────────────────────────────────────
export function usePreviewSegment() {
  return useMutation({
    mutationFn: (data) => waApi.previewSegment(data),
  });
}

export function useExpandSegment() {
  return useMutation({
    mutationFn: (data) => waApi.expandSegment(data),
  });
}
